import moment from "moment";
import { ReactNode, useMemo, useRef } from "react";
import { Cell, useBlockLayout, useTable } from "react-table";
import { useSticky } from "react-table-sticky";
import { ContractCreditBucket } from "src/api/credit/credit-types";
import { BaseButton, BaseTooltip } from "src/components";
import ScrollShadowProvider from "src/components/ScrollShadowProvider";
import { numberToStringWithComma } from "src/utils";

// 금액 포맷 (없으면 "-")
const formatAmount = (v?: number | string) => (v !== undefined && v !== null ? numberToStringWithComma(Number(v)) : "-");

// ⓘ 도움말이 붙는 헤더
const HelpHeader = ({ label, tooltip }: { label: string; tooltip: ReactNode }) => (
  <div className="flex-center no-wrap">
    <span>{label}</span>
    <BaseTooltip touchIcon="QUESTION" size={16} className="ml4">
      {tooltip}
    </BaseTooltip>
  </div>
);

interface IProps {
  buckets: ContractCreditBucket[];
  onClickCharge: (bucket: ContractCreditBucket) => void;
  onClickHistory: (bucket: ContractCreditBucket) => void;
}

/**
 * 계약 상세 > 충전금 관리 > 월별 충전금 내역 테이블 (RCL21)
 * - 가로 스크롤 + 우측 고정 컬럼(관리/내역)
 * - [지급/회수]는 현재·미래 정산월만 활성화 (과거월 비활성화)
 */
const CreditBucketTable = ({ buckets, onClickCharge, onClickHistory }: IProps) => {
  const currentYearMonth = Number(moment().format("YYYYMM")); // 과거월 지급/회수 비활성화 기준

  const columns = useMemo<Array<any>>(
    () => [
      {
        Header: "정산 년월",
        accessor: "yearMonth",
        width: 90,
        Cell: ({ value }: Cell<ContractCreditBucket>) => <p className="text-center">{value ?? "-"}</p>,
      },
      {
        Header: "총 충전금",
        accessor: "chargedCredit",
        width: 120,
        Cell: ({ value }: Cell<ContractCreditBucket>) => <p className="w-100 text-right">{formatAmount(value)}</p>,
      },
      {
        Header: "사용 충전금 (확정)",
        accessor: "confirmedCredit",
        width: 130,
        Cell: ({ value }: Cell<ContractCreditBucket>) => <p className="w-100 text-right">{formatAmount(value)}</p>,
      },
      {
        Header: () => (
          <HelpHeader
            label="사용 충전금 (예정)"
            tooltip={
              <ul className="base-list">
                <li className="text-primary">예약은 완료되었으나 아직 이용 전인 예약에 사용한 충전금입니다.</li>
                <li className="text-primary">정산이 '예정' 상태일 때 예약을 취소하면 자동으로 잔여 충전금에 반환됩니다.</li>
                <li className="text-primary">'확정' 이후에는 반환되지 않습니다.</li>
              </ul>
            }
          />
        ),
        accessor: "scheduledCredit",
        width: 140,
        Cell: ({ value }: Cell<ContractCreditBucket>) => <p className="w-100 text-right">{formatAmount(value)}</p>,
      },
      {
        Header: () => (
          <HelpHeader
            label="잔여 충전금"
            tooltip={
              <ul className="base-list">
                <li className="text-primary">현재 예약에 사용 가능한 충전금입니다.</li>
                <li className="text-primary">잔여 충전금 소진 후 발생하는 요금은 즉시 청구 대상 요금으로 기록됩니다.</li>
              </ul>
            }
          />
        ),
        accessor: "remainingCredit",
        width: 130,
        Cell: ({ value }: Cell<ContractCreditBucket>) => <p className="w-100 text-right">{formatAmount(value)}</p>,
      },
      {
        Header: "청구 대상 요금 (확정)",
        accessor: "confirmedAmount",
        width: 180,
        Cell: ({ value }: Cell<ContractCreditBucket>) => <p className="w-100 text-right">{formatAmount(value)}</p>,
      },
      {
        Header: () => (
          <HelpHeader
            label="청구 대상 요금 (예정)"
            tooltip={
              <ul className="base-list">
                <li className="text-primary">예약은 완료되었으나 아직 이용 전인 예약에 발생한 후불 요금입니다.</li>
                <li className="text-primary">정산이 '예정' 상태일 때 예약을 취소하면 자동으로 차감됩니다.</li>
                <li className="text-primary">'확정' 이후에는 차감되지 않습니다.</li>
              </ul>
            }
          />
        ),
        accessor: "scheduledAmount",
        width: 180,
        Cell: ({ value }: Cell<ContractCreditBucket>) => <p className="w-100 text-right">{formatAmount(value)}</p>,
      },
      {
        Header: () => <HelpHeader label="관리" tooltip="현재 월 및 미래 월만 [지급/회수]가 가능하며, 과거 월은 수정할 수 없습니다." />,
        id: "manage",
        width: 110,
        sticky: "right",
        Cell: ({ row }: Cell<ContractCreditBucket>) => {
          const bucket = row.original;
          const isPast = Number(String(bucket.yearMonth ?? "").replace(/\D/g, "")) < currentYearMonth;
          return (
            <div className="w-100 flex-center-center">
              <BaseButton title="지급/회수" className="size-small color-white" disabled={isPast} onClick={() => onClickCharge(bucket)} />
            </div>
          );
        },
      },
      {
        Header: "내역",
        id: "history",
        width: 110,
        sticky: "right",
        Cell: ({ row }: Cell<ContractCreditBucket>) => (
          <div className="w-100 flex-center-center">
            <BaseButton title="변경내역" className="size-small color-white" onClick={() => onClickHistory(row.original)} />
          </div>
        ),
      },
    ],
    [currentYearMonth, onClickCharge, onClickHistory],
  );

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({ columns, data: buckets }, useBlockLayout, useSticky);

  const tableRef = useRef<HTMLDivElement>(null);
  const tableWidth = useMemo(() => columns.reduce((sum, c: any) => sum + Number(c.width || 0), 0), [columns]);

  return (
    // 컨테이너를 925px로 제한 → 컬럼 합계(약 1,190px)가 넘쳐 base-table-scroll-warp에서 가로 스크롤 동작
    <div className="base-table-wrap pl0" style={{ maxWidth: 925, paddingRight: 0 }}>
      <ScrollShadowProvider tableRef={tableRef}>
        <div {...getTableProps()} className="base-table sticky" ref={tableRef}>
          <div className="header">
            {headerGroups.map((headerGroup) => (
              <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                {headerGroup.headers.map((header) => (
                  <div {...header.getHeaderProps()} className="base-table__th">
                    {header.render("Header")}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div {...getTableBodyProps()} className="body">
            {rows.map((row) => {
              prepareRow(row);
              return (
                <div {...row.getRowProps()} className="base-table__tr">
                  {row.cells.map((cell) => (
                    <div {...cell.getCellProps()} className="base-table__td">
                      {cell.render("Cell")}
                    </div>
                  ))}
                </div>
              );
            })}
            {rows.length === 0 && (
              <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                <div className="base-table__td w-100 text-center">
                  <div className="w-100 table-no-data">
                    <p>데이터가 없습니다.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </ScrollShadowProvider>
    </div>
  );
};

export default CreditBucketTable;
